import { FC, forwardRef } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import {
    Box,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    MenuList,
    ListSubheader,
    ListItemButtonBaseProps,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { logout, accessToken, refreshToken } from '@lendica/auth';
import { useAsyncError, DaylitLogo } from '@lendica/components';
import { useActiveCompanyKey } from '@lendica/hooks';
import { CompanySwitcher } from './CompanySwitcher';
import { DaylitTheme } from '@lendica/theme';
import { navigationConfig, NavigationItem } from '../config/navigation';

const NavigationMenuItem = forwardRef<
    HTMLLIElement,
    { path: string; onClick: () => void } & ListItemButtonBaseProps
>(({ path, onClick, ...props }, ref) => {
    const navigate = useNavigate();
    const companyKey = useActiveCompanyKey();
    const scopedPath = companyKey ? `/c/${companyKey}${path}` : path;
    const match = useMatch(`/c/:companyKey${path}/*`);

    const handleClick = () => {
        navigate(scopedPath);
        onClick();
    };

    return <MenuItem {...props} ref={ref} selected={!!match} onClick={handleClick} />;
});

interface MobileDropdownMenuProps {
    anchorEl: HTMLElement | null;
    open: boolean;
    onClose: () => void;
}

export const MobileDropdownMenu: FC<MobileDropdownMenuProps> = ({ anchorEl, open, onClose }) => {
    const navigate = useNavigate();
    const throwError = useAsyncError();

    const handleLogout = async () => {
        try {
            await logout('auth');
            refreshToken.remove();
            accessToken.remove();
        } catch (error: any) {
            console.log(error);
            if (error!.response && error!.response!.status === 401) {
                refreshToken.remove();
                accessToken.remove();
                navigate('/auth');
                return;
            }
            throwError(new Error(`Portal error on logout: ${error}`));
        }
        onClose();
    };

    const renderMenuItem = (item: NavigationItem) => {
        if (item.path) {
            return (
                <NavigationMenuItem
                    key={item.path}
                    path={item.path}
                    onClick={onClose}
                    data-testid={item.testId}
                >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                </NavigationMenuItem>
            );
        } else {
            return (
                <MenuItem key={item.label} onClick={item.onClick} data-testid={item.testId}>
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText primary={item.label} />
                </MenuItem>
            );
        }
    };

    return (
        <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            PaperProps={{
                sx: {
                    maxHeight: '80vh',
                    width: '280px',
                    mt: 1,
                    position: 'relative',
                },
            }}
            sx={{
                '& .MuiList-root': {
                    padding: 0,
                },
            }}
        >
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: 'background.paper',
                    zIndex: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <MenuItem
                    onClick={() => {
                        navigate('/');
                        onClose();
                    }}
                    sx={{ justifyContent: 'center' }}
                >
                    <DaylitLogo height={18} color={DaylitTheme.Palette.Burgundy} />
                </MenuItem>
            </Box>

            <MenuList dense sx={{ pt: 0 }}>
                <CompanySwitcher expanded={true} sx={{ borderRadius: '0px', py: 0.5 }} />
                <Divider />
                {navigationConfig.main.map(renderMenuItem)}

                <Divider />
                <ListSubheader component="div" sx={{ backgroundColor: 'inherit' }}>
                    Pay
                </ListSubheader>
                {navigationConfig.pay.map(renderMenuItem)}

                <ListSubheader component="div" sx={{ backgroundColor: 'inherit' }}>
                    Collect
                </ListSubheader>
                {navigationConfig.collect.map(renderMenuItem)}

                <Divider />
                {navigationConfig.other.map(item => (
                    <MenuItem
                        key={item.label}
                        onClick={() => {
                            item.onClick?.();
                            onClose();
                        }}
                    >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText primary={item.label} />
                    </MenuItem>
                ))}

                <ListSubheader component="div" sx={{ backgroundColor: 'inherit' }}>
                    Settings
                </ListSubheader>
                {navigationConfig.settings.map(renderMenuItem)}

                <MenuItem onClick={handleLogout} data-testid="logout-button">
                    <ListItemIcon>
                        <LogoutIcon />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                </MenuItem>
            </MenuList>
        </Menu>
    );
};
